'use client'

import { useState, useEffect, useRef, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Menu, X, ArrowRight, Calendar } from 'lucide-react'
import { NAV_LINKS, BOOK_A_DEMO_URL, APP_URL } from '@/lib/constants'
import { DemoBookingModal } from '@/components/conversion/DemoBookingModal'

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [demoOpen, setDemoOpen] = useState(false)

  useEffect(() => {
    let rafId: number
    const handleScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => setScrolled(window.scrollY > 20))
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
      cancelAnimationFrame(rafId)
    }
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-black/80 backdrop-blur-xl border-b border-white/[0.06]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="relative z-10 flex-shrink-0">
            <Image
              src="/assets/images/nav-logo-file.webp"
              alt="BuilderLync"
              width={180}
              height={38}
              priority
              style={{ height: '38px', width: 'auto' }}
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="py-2 text-[0.9rem] font-medium text-zinc-400 hover:text-white transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
            >
              Login
            </a>
            <button
              onClick={() => setDemoOpen(true)}
              className="btn-primary text-sm px-5 py-2.5 group"
            >
              <Calendar size={14} />
              <span>Book a Demo</span>
              <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden relative z-10 p-2 -mr-2 text-zinc-400 hover:text-white transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl lg:hidden transition-[opacity,visibility] duration-200"
        style={{
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
        }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-2 px-8">
          {NAV_LINKS.map((link, i) => (
            <div
              key={link.href}
              className="transition-all duration-300"
              style={{
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
                transitionDelay: isOpen ? `${i * 0.05 + 0.1}s` : '0s',
              }}
            >
              <Link
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="block text-2xl font-semibold text-white hover:text-red-500 transition-colors py-3"
              >
                {link.label}
              </Link>
            </div>
          ))}

          <div
            className="flex flex-col items-center gap-4 mt-8 w-full max-w-xs transition-all duration-300"
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'translateY(0)' : 'translateY(20px)',
              transitionDelay: isOpen ? '0.4s' : '0s',
            }}
          >
            <button
              onClick={() => {
                setIsOpen(false)
                setTimeout(() => setDemoOpen(true), 200)
              }}
              className="btn-primary w-full justify-center text-base"
            >
              <Calendar size={16} />
              Book a Demo
              <ArrowRight size={16} />
            </button>
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full justify-center text-base"
              onClick={() => setIsOpen(false)}
            >
              Login
            </a>
          </div>
        </div>
      </div>

      {/* Demo Booking Modal */}
      <DemoBookingModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} page="navbar" />
    </>
  )
}
